import { authReady, getToken, refreshToken } from './keycloakService'
import { getEmbedRuntime } from '@/embed/runtime'
import type { Zone, Reservation, ReservationEvent, AvailableSpace, ReservationFormData, GuestEntry, ReservationWarning, NotificationChannel, CheckedReservation, DetectedViolation, TimeRangeCheck, SpaceBlock, BlockKind, BlockedSpace } from '@/types'

const DEFAULT_API_BASE = import.meta.env.VITE_API_URL || '/v1/reservations'

/** Where this request should go. The host may point the module at its own proxy. */
export function apiBase(): string {
  return getEmbedRuntime()?.apiBase || DEFAULT_API_BASE
}

function sanitizeGuestPhone(value?: string): string | undefined {
  const trimmed = value?.trim()
  if (!trimmed) return undefined

  // Prevent submitting dial-code-only values (e.g. "+1", "+44").
  if (/^\+\d{1,3}$/.test(trimmed)) return undefined

  const digits = trimmed.replace(/\D/g, '')
  if (!digits) return undefined

  return trimmed
}

/**
 * Whether this guest is to be texted: a phone number and a channel that
 * includes SMS. The API sends email to a guest without a phone whatever the
 * channel says, so only then is consent asked for and sent.
 */
export function guestGetsTexts(guest: GuestEntry): boolean {
  return !!sanitizeGuestPhone(guest.guestPhone) && (guest.notificationChannel === 'sms' || guest.notificationChannel === 'both')
}

/** A local date and time as ISO 8601 with this browser's UTC offset. */
export function buildOffsetIso(date: string, time: string): string {
  // Build local wall-clock datetime and keep the user's UTC offset.
  const [year, month, day] = date.split('-').map(Number)
  const [hour, minute] = time.split(':').map(Number)
  const dt = new Date(year, month - 1, day, hour, minute, 0)

  const offsetMinutes = -dt.getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const absMinutes = Math.abs(offsetMinutes)
  const offsetHours = String(Math.floor(absMinutes / 60)).padStart(2, '0')
  const offsetMins = String(absMinutes % 60).padStart(2, '0')

  const yyyy = String(dt.getFullYear())
  const mm = String(dt.getMonth() + 1).padStart(2, '0')
  const dd = String(dt.getDate()).padStart(2, '0')
  const hh = String(dt.getHours()).padStart(2, '0')
  const mi = String(dt.getMinutes()).padStart(2, '0')

  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:00${sign}${offsetHours}:${offsetMins}`
}

/**
 * The X-Active-Org value for this session, or null.
 *
 * Unified X-Active-Org header (keycloak/AUTHENTICATION.md §5.1). Reserve tokens
 * are normally single-org so the backend derives the active Org from the
 * `organization` claim; but when a user has switched contexts via the upcoming
 * OrgSwitcher the value lives under parkeze.active_org.
 *
 * Embedded, the active org comes from the host — its operator switcher is the
 * one the user actually operated. Standalone, it comes from storage.
 */
export function activeOrg(): string | null {
  const embed = getEmbedRuntime()
  if (embed) return embed.activeOrg || null
  try {
    const stored =
      localStorage.getItem('parkeze.active_org') ||
      localStorage.getItem('admin-operator-context')
    if (!stored) return null
    return stored === 'view-all' ? '*' : stored
  } catch {
    return null
  }
}

/**
 * The Authorization, X-Active-Org and X-Station headers `apiFetch`
 * sends, for a caller that cannot go through it -- the kiosk's live streams are
 * long-lived XHRs -- so they authenticate as exactly the same session.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const embed = getEmbedRuntime()
  if (!embed) await authReady
  const headers: Record<string, string> = {}
  const token = embed ? embed.getToken() : getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  const org = activeOrg()
  if (org) headers['X-Active-Org'] = org
  // The station the person chose narrows them to its zones (decisions 22, 24).
  const station = embed?.station?.()
  if (station) headers['X-Station'] = station
  return headers
}

/**
 * The body as JSON, or an error that says what came back instead.
 *
 * A static host answers a path it does not know with its own index.html and a
 * 200. Parsed as JSON that fails with "Unexpected token '<'", which says nothing
 * useful. And the callers default a failed read to an empty list, so the
 * operator console's mount of this wizard, pointed at the wrong base URL,
 * reported "No reservable zones available" to everybody.
 */
async function readJson<T>(resp: Response): Promise<T> {
  const type = resp.headers.get('content-type') || ''
  if (!type.includes('json')) {
    throw new Error(
      `Expected JSON from ${resp.url} but got ${type || 'no content type'}. The reservation API address may be wrong.`,
    )
  }
  return resp.json()
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const embed = getEmbedRuntime()
  const base = apiBase()
  // Embedded, the host authenticated already; authHeaders does not wait on a
  // local session that would never resolve and hang the module.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
    ...(await authHeaders()),
  }

  let resp = await fetch(`${base}${path}`, { ...options, headers })

  // Auto-refresh on 401. Only whoever owns the session can renew it.
  if (resp.status === 401 && headers['Authorization']) {
    const refreshed = embed
      ? await (embed.onTokenExpired?.(headers['Authorization'].slice('Bearer '.length)) ?? Promise.resolve(false))
      : await refreshToken()
    if (refreshed) {
      const newToken = embed ? embed.getToken() : getToken()
      if (newToken) headers['Authorization'] = `Bearer ${newToken}`
      resp = await fetch(`${base}${path}`, { ...options, headers })
    }
  }

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({ message: resp.statusText }))
    const message = body.message || `API error: ${resp.status}`
    // Status and code ride along so a caller can tell "sign in again" or "not
    // allowed" from a failure worth showing.
    throw Object.assign(new Error(message), { status: resp.status, code: body.error })
  }

  return readJson<T>(resp)
}

// --- Zones ---
export function fetchZones(): Promise<Zone[]> {
  return apiFetch<{ zones: Zone[] }>('/zones').then((r) => r.zones || [])
}

/**
 * A zone's spaces and whether each is free from `start` to `end`. Send both
 * with a UTC offset (`buildOffsetIso`): without one the API has to guess the
 * zone. `excludeReservationId` is the booking being edited, whose own hold
 * then does not count as taking its space.
 */
export function fetchAvailableSpaces(
  zoneId: string,
  start: string,
  end: string,
  excludeReservationId?: string,
): Promise<AvailableSpace[]> {
  const params = new URLSearchParams({ from: start, until: end })
  if (excludeReservationId) params.set('exclude_reservation_id', excludeReservationId)
  return apiFetch<{ spaces: AvailableSpace[] }>(`/zones/${encodeURIComponent(zoneId)}/spaces?${params}`).then(
    (r) => r.spaces || [],
  )
}

// --- Reservations ---
export function fetchReservations(params?: Record<string, string>): Promise<{ reservations: Reservation[]; total: number }> {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return apiFetch(`/${qs}`)
}

/**
 * Every booking overlapping a window, each with its `available_actions`.
 *
 * The API answers at most 100 a page, so this reads pages until one comes back
 * short. Capped at 2000: a month of a busy lot, not the whole history.
 */
export async function fetchReservationsInRange(from: string, until: string): Promise<Reservation[]> {
  const PER_PAGE = 100
  const all: Reservation[] = []
  for (let page = 1; page <= 20; page++) {
    const { reservations } = await fetchReservations({ from, until, per_page: String(PER_PAGE), page: String(page) })
    all.push(...reservations)
    if (reservations.length < PER_PAGE) break
  }
  return all
}

/** One booking, with the `available_actions` the calling token has on it. */
export function fetchReservation(id: string): Promise<Reservation> {
  return apiFetch(`/${encodeURIComponent(id)}`)
}

/** What has happened to a booking, oldest first. */
export function fetchReservationEvents(id: string): Promise<ReservationEvent[]> {
  return apiFetch<{ events: ReservationEvent[] }>(`/${encodeURIComponent(id)}/events`).then(
    (r) => r.events || [],
  )
}

export async function createReservations(data: ReservationFormData): Promise<{ reservations: Reservation[]; warnings: ReservationWarning[] }> {
  const reservations: Reservation[] = []
  const warnings: ReservationWarning[] = []
  const reservedFrom = buildOffsetIso(data.date, data.startTime)
  const reservedUntil = buildOffsetIso(data.date, data.endTime)
  // The note goes on every guest's booking, with its switch; an empty one is
  // no note at all, which is what the API stores for it anyway.
  const notes = data.notes.trim() || undefined
  for (const guest of data.guests) {
    const combinedGuestName = `${guest.guestFirstName || ''} ${guest.guestLastName || ''}`.trim() || guest.guestName
    const guestPhone = sanitizeGuestPhone(guest.guestPhone)
    const resp = await apiFetch<Reservation & { warnings?: ReservationWarning[] }>('/', {
      method: 'POST',
      body: JSON.stringify({
        zone_id: data.zoneId,
        sensor_id: guest.sensorId,
        reserved_from: reservedFrom,
        reserved_until: reservedUntil,
        guest_name: combinedGuestName,
        guest_email: guest.guestEmail,
        guest_phone: guestPhone,
        notification_channel: guest.notificationChannel || undefined,
        // Required by the API whenever the channel includes SMS; the wizard
        // does not go on without it.
        sms_consent: guestGetsTexts(guest) && guest.smsConsent ? true : undefined,
        vehicle_make: guest.vehicleMake || undefined,
        vehicle_model: guest.vehicleModel || undefined,
        vehicle_color: guest.vehicleColor || undefined,
        vehicle_lpn: guest.vehicleLpn || undefined,
        notes,
        notes_guest_visible: notes ? data.notesGuestVisible : false,
      }),
    })
    const { warnings: guestWarnings, ...reservation } = resp
    reservations.push(reservation as Reservation)
    if (guestWarnings && guestWarnings.length) {
      for (const w of guestWarnings) {
        warnings.push({ ...w, message: `${combinedGuestName || guest.guestEmail}: ${w.message}` })
      }
    }
  }
  return { reservations, warnings }
}

export function cancelReservation(id: string): Promise<void> {
  return apiFetch(`/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/**
 * What PUT /v1/reservations/<id> may change. A field left out is left as it
 * was; `null` clears a phone, a vehicle field or the note. Not `status`: that
 * changes only by approve, reject and cancel.
 */
export interface ReservationPatch {
  reserved_from?: string
  reserved_until?: string
  /** A new space; there is no way to take a booking's space away. */
  sensor_id?: string
  guest_name?: string
  guest_email?: string
  guest_phone?: string | null
  notification_channel?: NotificationChannel
  /**
   * The person confirms the guest agreed to texts. The API requires it when
   * the update sets a channel that texts, or gives a texted guest a new
   * number, and records it afresh then.
   */
  sms_consent?: true
  vehicle_make?: string | null
  vehicle_model?: string | null
  vehicle_color?: string | null
  vehicle_lpn?: string | null
  notes?: string | null
  notes_guest_visible?: boolean
}

/**
 * Change a booking: only the fields in `patch`, which should be only the ones
 * that changed (`reservationPatch`), so the history, the guest's message and
 * the consent rules see exactly what the person changed. A booking that has
 * ended or was voided takes only the note (decision 28).
 */
export function updateReservation(
  id: string,
  patch: ReservationPatch,
): Promise<{
  message: string
  reservation_id: string
  /** The status after the change: `pending_approval` when a move needs approval again. */
  status?: Reservation['status']
  warnings?: ReservationWarning[]
}> {
  return apiFetch(`/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(patch) })
}

/** A booking as the Edit form holds it: local wall-clock date and times, and text fields. */
export interface ReservationDraft {
  date: string
  startTime: string
  /** The end's date: the start's, unless the booking runs past midnight. */
  endDate: string
  endTime: string
  /** '' when the booking has no space. */
  sensorId: string
  guestName: string
  guestEmail: string
  guestPhone: string
  notificationChannel: NotificationChannel
  smsConsent: boolean
  vehicleMake: string
  vehicleModel: string
  vehicleColor: string
  vehicleLpn: string
  notes: string
  notesGuestVisible: boolean
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** An instant as this browser's local date (YYYY-MM-DD) and time (HH:MM). */
export function localParts(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  return {
    date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
  }
}

export function draftFromReservation(r: Reservation): ReservationDraft {
  const from = localParts(r.reserved_from)
  const until = localParts(r.reserved_until)
  return {
    date: from.date,
    startTime: from.time,
    endDate: until.date,
    endTime: until.time,
    sensorId: r.space_uuid || '',
    guestName: r.guest_name || '',
    guestEmail: r.guest_email || '',
    guestPhone: r.guest_phone || '',
    notificationChannel: r.notification_channel || 'email',
    smsConsent: false,
    vehicleMake: r.vehicle_make || '',
    vehicleModel: r.vehicle_model || '',
    vehicleColor: r.vehicle_color || '',
    vehicleLpn: r.vehicle_lpn || '',
    notes: r.notes || '',
    notesGuestVisible: !!r.notes_guest_visible,
  }
}

/** Digits, with a bare ten-digit number read as North American (+1). */
function phoneDigits(value: string): string {
  const digits = value.replace(/\D/g, '')
  return !value.trim().startsWith('+') && digits.length === 10 ? `1${digits}` : digits
}

/** The channel the booking will have: email for a guest left without a phone. */
function effectiveChannel(d: ReservationDraft): NotificationChannel {
  return sanitizeGuestPhone(d.guestPhone) ? d.notificationChannel : 'email'
}

/**
 * Whether saving `draft` needs the guest's consent to texts: they are to be
 * texted, and either the channel changes to one that texts or the number
 * changes. reservation-api draws the same line and refuses the save without
 * `sms_consent: true`.
 */
export function editAsksSmsConsent(before: ReservationDraft, draft: ReservationDraft): boolean {
  const channel = effectiveChannel(draft)
  if (channel !== 'sms' && channel !== 'both') return false
  return channel !== effectiveChannel(before) || phoneDigits(draft.guestPhone) !== phoneDigits(before.guestPhone)
}

const textOrNull = (value: string) => value.trim() || null

/** The fields `draft` changed from `before`, as PUT takes them. Empty when nothing changed. */
export function reservationPatch(before: ReservationDraft, draft: ReservationDraft): ReservationPatch {
  const patch: ReservationPatch = {}
  if (draft.date !== before.date || draft.startTime !== before.startTime) {
    patch.reserved_from = buildOffsetIso(draft.date, draft.startTime)
  }
  if (draft.endDate !== before.endDate || draft.endTime !== before.endTime) {
    patch.reserved_until = buildOffsetIso(draft.endDate, draft.endTime)
  }
  if (draft.sensorId && draft.sensorId !== before.sensorId) patch.sensor_id = draft.sensorId
  if (draft.guestName.trim() !== before.guestName.trim()) patch.guest_name = draft.guestName.trim()
  if (draft.guestEmail.trim() !== before.guestEmail.trim()) patch.guest_email = draft.guestEmail.trim()
  if (phoneDigits(draft.guestPhone) !== phoneDigits(before.guestPhone)) {
    patch.guest_phone = sanitizeGuestPhone(draft.guestPhone) ?? null
  }
  if (effectiveChannel(draft) !== effectiveChannel(before)) patch.notification_channel = effectiveChannel(draft)
  if (editAsksSmsConsent(before, draft) && draft.smsConsent) patch.sms_consent = true
  const vehicle = [
    ['vehicle_make', 'vehicleMake'],
    ['vehicle_model', 'vehicleModel'],
    ['vehicle_color', 'vehicleColor'],
    ['vehicle_lpn', 'vehicleLpn'],
  ] as const
  for (const [field, key] of vehicle) {
    if (draft[key].trim() !== before[key].trim()) patch[field] = textOrNull(draft[key])
  }
  if (draft.notes.trim() !== before.notes.trim()) patch.notes = textOrNull(draft.notes)
  if (draft.notesGuestVisible !== before.notesGuestVisible) patch.notes_guest_visible = draft.notesGuestVisible
  return patch
}

export function deleteReservationPermanently(id: string): Promise<{ message: string; reservation_id: string }> {
  const params = new URLSearchParams({ hard_delete: 'true' })
  return apiFetch(`/${encodeURIComponent(id)}?${params.toString()}`, { method: 'DELETE' })
}
export function approveReservation(id: string): Promise<{ message: string }> {
  return apiFetch(`/${encodeURIComponent(id)}/approve`, { method: 'POST' })
}

export function rejectReservation(id: string, reason?: string): Promise<{ message: string }> {
  return apiFetch(`/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || '' }),
  })
}

export function resendReservationNotification(id: string): Promise<{ message: string; reservation_id: string; event_type: string }> {
  return apiFetch(`/${encodeURIComponent(id)}/resend`, { method: 'POST' })
}

// --- Enforcement checks ---
//
// reservation-api's officer endpoints. Each requires `enforcement.check`; the
// kiosk is only offered to those accounts.

/** Bookings for a plate, each saying whether it permits parking right now. */
export function checkPlate(lpn: string): Promise<{ reservations: CheckedReservation[] }> {
  return apiFetch(`/check/lpn?${new URLSearchParams({ lpn })}`)
}

/**
 * Everything booked or blocked in a window.
 *
 * The kiosk's day calendar reads this rather than the reservation list:
 * listing reservations is scoped by the caller's own reservation roles, and a
 * compliance-only officer may see only their own. This is the officer's view
 * of every booking, which is the one the calendar needs.
 */
export function checkTimeRange(from: string, until: string, zoneId?: string): Promise<TimeRangeCheck> {
  const params = new URLSearchParams({ from, until })
  if (zoneId) params.set('zone_id', zoneId)
  return apiFetch(`/check/time-range?${params}`)
}

/** Violations the sensors are reporting as still happening. */
export function fetchOpenViolations(zoneId?: string): Promise<DetectedViolation[]> {
  const params = new URLSearchParams({ open: 'true' })
  if (zoneId) params.set('zone_id', zoneId)
  return apiFetch<{ violations: DetectedViolation[] }>(`/check/violations?${params}`).then(
    (r) => r.violations || [],
  )
}

// --- Blocks (event and facilities) ---
//
// An organization's administrator (logbook decision 24); the API decides.

/** Event and facilities blocks overlapping a window, one entry per block. */
export function fetchBlocks(from: string, until: string, zoneId?: string): Promise<SpaceBlock[]> {
  const params = new URLSearchParams({ from, until })
  if (zoneId) params.set('zone_id', zoneId)
  return apiFetch<{ blocks: SpaceBlock[] }>(`/blocks?${params}`).then((r) => r.blocks || [])
}

export function createBlock(body: {
  zone_id: string
  kind: BlockKind
  from: string
  until: string
  notes: string
  lpns: string[]
  space_ids?: string[]
}): Promise<{ block: SpaceBlock; blocked: BlockedSpace[] }> {
  return apiFetch('/blocks', { method: 'POST', body: JSON.stringify(body) })
}

/** End a block now (or cancel it before it starts). */
export function endBlock(blockId: string): Promise<{ block_id: string; ended: number }> {
  return apiFetch(`/blocks/${encodeURIComponent(blockId)}`, { method: 'DELETE' })
}
